import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import t from "../../../app/i18n";
import { calcDraftsApi } from "../../../modules/calculator/drafts/api";
import {
  CalcDraftFilterPageRequest,
  CalcDraftFilterPageResult,
  CreateUpdateCalcDraft,
} from "../../../modules/calculator/drafts/types";
import { UUID } from "../../../typings/global";
import { EntityObject, TwoLevelEntityIdObject } from "../../types";

export const CALC_DRAFTS_QUERY_KEYS = {
  filterCalcDrafts: (payload?: CalcDraftFilterPageRequest) =>
    payload ? ["filterCalcDrafts", payload] : ["filterCalcDrafts"],
  getCalcDraft: (id: UUID) => ["getCalcDraft", id],
  createCalcDraft: () => ["createCalcDraft"],
  updateCalcDraft: () => ["updateCalcDraft"],
  deleteCalcDraft: () => ["deleteCalcDraft"],
  uploadCalcDraftAttachments: () => ["uploadCalcDraftAttachments"],
  deleteCalcDraftAttachment: () => ["deleteCalcDraftAttachment"],
};

export const useFilterCalcDrafts = (payload: CalcDraftFilterPageRequest) => {
  return useQuery({
    queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts(payload),
    queryFn: async () => {
      const { data } = await calcDraftsApi.filterCalcDrafts(payload);

      return data;
    },
    enabled: !!payload.calcTypes.length,
  });
};

export const useGetCalcDraft = (id: UUID) => {
  return useQuery({
    queryKey: CALC_DRAFTS_QUERY_KEYS.getCalcDraft(id),
    queryFn: async () => {
      const { data } = await calcDraftsApi.getCalcDraft({ id });

      return data;
    },
    enabled: !!id,
    retry: 1,
  });
};

export const useCreateCalcDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_DRAFTS_QUERY_KEYS.createCalcDraft(),
    mutationFn: async (payload: CreateUpdateCalcDraft) => {
      const { data } = await calcDraftsApi.createCalcDraft(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts() });
      queryClient.setQueryData(CALC_DRAFTS_QUERY_KEYS.getCalcDraft(data.id), data);

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("calc.draft.helpers.draftSaved"),
      });
    },
  });
};

export const useUpdateCalcDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_DRAFTS_QUERY_KEYS.updateCalcDraft(),
    mutationFn: async (payload: EntityObject<CreateUpdateCalcDraft>) => {
      const { data } = await calcDraftsApi.updateCalcDraft(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts() });
      queryClient.setQueryData(CALC_DRAFTS_QUERY_KEYS.getCalcDraft(data.id), data);

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("calc.draft.helpers.draftSaved"),
      });
    },
  });
};

export const useDeleteCalcDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_DRAFTS_QUERY_KEYS.deleteCalcDraft(),
    mutationFn: (id: UUID) => {
      return calcDraftsApi.deleteCalcDraft({ id });
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts() });
    },
  });
};

export const useUploadCalcDraftAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_DRAFTS_QUERY_KEYS.uploadCalcDraftAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await calcDraftsApi.uploadCalcDraftAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      const queries = queryClient.getQueriesData({ queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts() });

      queries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (oldData: CalcDraftFilterPageResult) => ({
          ...oldData,
          pageData: oldData.pageData.map((item) => {
            if (item.id === mutatedPayload.id) {
              return { ...item, attachments: data };
            }

            return item;
          }),
        }));
      });
    },
  });
};

export const useDeleteCalcDraftAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_DRAFTS_QUERY_KEYS.deleteCalcDraftAttachment(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await calcDraftsApi.deleteCalcDraftAttachment(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      const queries = queryClient.getQueriesData({ queryKey: CALC_DRAFTS_QUERY_KEYS.filterCalcDrafts() });

      queries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (oldData: CalcDraftFilterPageResult) => ({
          ...oldData,
          pageData: oldData.pageData.map((item) => {
            if (item.id === mutatedPayload.id1) {
              return {
                ...item,
                attachments: item.attachments.filter((attachment) => attachment.id !== mutatedPayload.id2),
              };
            }

            return item;
          }),
        }));
      });
    },
  });
};
